/**
 * Projects (docs/projects.md, flag `projectsUI`; migration 0060). Owner, 2026-11-02: "Rather than big
 * task it should be called a project and you can partially complete it, add a progress photo", then
 * picking "Grows as you work" for the gold: a project does not age like a task; each progress photo adds
 * GOLD.projectProgress to its pot, and the whole pot is split between everyone who helped at the finish
 * (the shared credit split, shared/shares.ts).
 *
 * A project is a big job (`tasks.big_job`) seen with the flag on: the data keeps its name, the words
 * change. The pot rule pays only while the flag is on; with it off a big job ages and pays as before.
 * Pure, so the Node tests import it by extension.
 */
import { GOLD, type BountyTask } from "./bounty.ts";
import { localDay } from "./flash.ts";
import { sameShareName } from "./shares.ts";
import type { Role } from "./members.ts";
import type { Task, TaskProgress } from "./types";

/** The written words. Plain, as the app speaks; nothing here is said aloud. */
export const PROJECT_WORDS = {
  /** The pill on the photo check and the schedule form, and the schedule list's line. */
  label: "Project",
  /** The weekly scroll's tally label. */
  plural: "Projects",
  /** The task screen's word in the meta line for an open project. */
  meta: "Project",
  /** The strip of progress photos' label. */
  progress: "Progress",
  /** The button under Job's Done that takes a progress photo. */
  add: "Add progress",
  /** The photo check's confirm for a progress photo. */
  confirm: "Add it",
  /** Said under the button once a photo is in and the pot did not grow (a second photo the same day). */
  added: "Progress added.",
  /** The quiet word under one's own progress photo (any, for a steward), and the confirm's yes in the app. */
  remove: "Remove",
  /** The confirm's no in the app. */
  keep: "Keep it",
  /** The confirm before a progress photo comes off. */
  removeQuestion: "Remove this progress photo? Its gold leaves the pot.",
} as const;

/**
 * What a progress photo's pick is for, kept with the pick so a photo Android hands back after killing
 * the app behind the camera comes back to Add progress and never to Job's Done on the same screen (the
 * restore matches by route; both pickers share the task's).
 */
export const PROGRESS_PICK = "progress";

/** The words with the flag off: a big job stays a big job. */
const OLD_WORDS = { label: "Big job", plural: "Big jobs" } as const;

/** "Project" with projectsUI on, "Big job" with it off: the pill, the schedule line. */
export function jobSizeLabel(projectsUI: boolean): string {
  return projectsUI ? PROJECT_WORDS.label : OLD_WORDS.label;
}

/** "Projects" with projectsUI on, "Big jobs" with it off: the weekly scroll's tally label. */
export function jobSizePlural(projectsUI: boolean): string {
  return projectsUI ? PROJECT_WORDS.plural : OLD_WORDS.plural;
}

/** Said under Add progress once a photo is in: "+20 gold in the pot." when it grew, "Progress added." when not. */
export function progressAddedLine(gold: number): string {
  return gold > 0 ? `+${gold} gold in the pot.` : PROJECT_WORDS.added;
}

/** True for a task that is a project with the flag on: a big job, never a luxury task. */
export function isProject(task: Pick<Task, "big_job" | "luxury">, projectsUI: boolean): boolean {
  return projectsUI && !!task.big_job && !task.luxury;
}

/** The gold a project's progress photos have grown its pot by: the sum of each photo's own gold. */
export function projectGrowth(progress: ReadonlyArray<Pick<TaskProgress, "gold">> | null | undefined): number {
  return (progress ?? []).reduce((sum, p) => sum + (Number.isFinite(p.gold) ? Math.max(0, p.gold) : 0), 0);
}

/**
 * The task as bountyFor reads it: with projectsUI on and a big job in hand, `project` carries its pot's
 * growth so it pays as a project; otherwise `project` is null and it pays as before.
 */
export function asBountyTask<T extends BountyTask & Partial<Pick<Task, "progress">>>(task: T, projectsUI: boolean): T & { project: number | null } {
  const project = projectsUI && task.big_job && !task.luxury ? projectGrowth(task.progress) : null;
  return { ...task, project };
}

/** The pot a project shows while open: the big-job base and its growth (bonuses for the finisher aside). */
export function projectPot(task: Partial<Pick<Task, "progress">>): number {
  return GOLD.bigJobBase + projectGrowth(task.progress);
}

/**
 * The gold a new progress photo by `name` at `at` grows the pot by: GOLD.projectProgress for that peon's
 * first photo on this project in the household's day (`tz`), else 0, so posting more photos the same
 * day adds pictures, not gold. A photo taken off since gives its day back.
 */
export function progressGold(existing: ReadonlyArray<Pick<TaskProgress, "peon_name" | "created_at" | "gold">>, name: string, at: number, tz: string): number {
  const today = localDay(new Date(at), tz);
  const grewToday = existing.some((p) => p.gold > 0 && sameShareName(p.peon_name, name) && localDay(new Date(Date.parse(p.created_at)), tz) === today);
  return grewToday ? 0 : GOLD.projectProgress;
}

/** The peons who posted progress on a project, each once, in the order they first did. */
export function progressPosters(progress: ReadonlyArray<Pick<TaskProgress, "peon_name">> | null | undefined): string[] {
  const out: string[] = [];
  for (const p of progress ?? []) if (p.peon_name && !out.some((n) => sameShareName(n, p.peon_name))) out.push(p.peon_name);
  return out;
}

/** Who may take a progress photo off: whoever posted it, or a steward. */
export function canRemoveProgress(p: Pick<TaskProgress, "peon_name">, name: string, role: Role | null | undefined): boolean {
  return role === "steward" || sameShareName(p.peon_name, name);
}

/** How many progress photos one project keeps at most. */
export const PROGRESS_MAX = 60;

/** The refusals. */
export const PROGRESS_NOT_PROJECT = "This isn't a project.";
export const PROGRESS_NOT_OPEN = "This project is done.";
export const PROGRESS_FULL = "This project has all the photos it can hold.";
export const PROGRESS_NO_NAME = "Who is posting this?";
export const PROGRESS_GONE = "That photo is gone.";
export function progressRefusal(poster: string): string {
  return `Only ${poster} or a steward can remove this photo.`;
}

/** The push to the household, everyone but the poster: "Progress: Clean the garage" / "From Hue. The pot is 140 gold." */
export function progressPushTitle(title: string): string {
  return `Progress: ${title}`;
}
export function progressPushBody(from: string, pot: number): string {
  return `From ${from}. The pot is ${pot} gold.`;
}
